import { html, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { PopBaseComponent } from '../../base/pop-base-component';
import style from './risk-table.scss?inline';
import type { RiskTable } from './risk-table.model';
import { Constants } from './risk-table.constants';
import { Constants as BaseConstants } from '../../base/base.constants.js';
import {
  formatDecimalAsPercentage,
  formatToNDecimalPlaces,
} from '../../common/utils/formatNumber';
import {
  ensureParagraphTag,
  toSentenceCase,
} from '../../common/utils/textFormat';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { sanitizeHTML } from '../../common/utils/sanitizeHTML';

/**
 * @tag pop-risk-table-component
 * @property {RiskTable} [componentObject] Risk table content
 * @attribute {RiskTable} [data-component-object] Risk table content
 */
@customElement(Constants.TAG_NAME)
export class PopRiskTableComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  @property({
    attribute: BaseConstants.DATA_COMPONENT_OBJECT,
    type: Object,
  })
  componentObject?: RiskTable;

  /**
   * @internal
   */
  static readonly tagName = Constants.TAG_NAME;

  /**
   * @internal
   */
  componentName = PopRiskTableComponent.tagName;

  renderInfoIcon() {
    return html`<beacon-icon slot="icon" name="information"></beacon-icon>`;
  }

  private createTableHeader(
    columnId: string,
    columnName: string,
    superscript?: string,
  ) {
    const iconButtonLabel = `${columnName} details`;

    // The content for the default slot needs the attribute `slot=""` in
    // order to be found in Serenity tests.

    return html`
      <th id="${columnId}-column" scope="col">
        <beacon-table-header variant="normal">
          ${superscript
            ? html`<span slot="">${columnName}<sup>${superscript}</sup></span>`
            : html`<span slot="">${columnName}</span>`}

          <beacon-icon-button
            id="${columnId}-button"
            type="button"
            slot="action-zone"
            class="tooltip-icon-button"
            variant="tertiary"
          >
            ${iconButtonLabel} ${this.renderInfoIcon()}
          </beacon-icon-button>
        </beacon-table-header>
      </th>
    `;
  }

  private createPopoverContent(columnId: string, tooltipContent?: string) {
    return html`
      <beacon-popover opens-by="#${columnId}-button">
        ${unsafeHTML(
          sanitizeHTML(
            ensureParagraphTag(tooltipContent ?? Constants.NO_DATA_MESSAGE),
          ),
        )}
      </beacon-popover>
    `;
  }

  private getRiskGlossaryByID(id: string) {
    return this.componentObject?.riskFactorGlossary[id];
  }

  private renderRiskGlossaryPopovers() {
    const risks = [...(this.componentObject?.risks ?? [])];

    if (risks.length > 0) {
      return html`
        ${risks.map((risk) => {
          return this.createPopoverContent(
            risk.id,
            this.getRiskGlossaryByID(risk.id),
          );
        })}
      `;
    }

    return html``;
  }

  private renderTableHead() {
    const risks = [...(this.componentObject?.risks ?? [])];

    if (!risks || risks.length === 0) {
      return html`
        <tr>
          <td>
            <beacon-table-header>
              ${Constants.NO_DATA_MESSAGE}
            </beacon-table-header>
          </td>
        </tr>
      `;
    }

    risks.sort((a, b) => a.rank - b.rank);

    return html`
      <tr>
        <th></th>
        ${risks.map((risk) => {
          return this.createTableHeader(
            risk.id,
            toSentenceCase(risk.label ?? ''),
          );
        })}
      </tr>
    `;
  }

  private renderEmptyTableCell() {
    return html` <td>
      <beacon-table-record variant="numeral">
        ${Constants.EMPTY_CELL_VALUE}
      </beacon-table-record>
    </td>`;
  }

  private renderTableCell(riskValue: number | undefined, isPercentage = false) {
    if (riskValue === undefined) {
      return this.renderEmptyTableCell();
    } else {
      return html`
        <td>
          <beacon-table-record variant="numeral">
            ${isPercentage
              ? formatDecimalAsPercentage(riskValue, 2)
              : formatToNDecimalPlaces(riskValue, 2)}
          </beacon-table-record>
        </td>
      `;
    }
  }

  private renderTableBody() {
    const risks = [...(this.componentObject?.risks ?? [])];

    if (!risks || risks.length === 0) {
      return html``;
    }

    risks.sort((a, b) => a.rank - b.rank);

    return html`
      <tr>
        <th>
          <beacon-table-stub>
            <span
              slot="title"
              class="b-typography-sans-serif--body--100 b-font-weight-bold"
            >
              ${this.componentObject?.productName}
            </span>
          </beacon-table-stub>
        </th>
        ${risks.map((risk) => {
          return this.renderTableCell(risk.fundValue, risk.displayPercentage);
        })}
      </tr>
      <tr>
        <th>
          <beacon-table-stub>
            <span
              slot="title"
              class="b-typography-sans-serif--body--100 b-font-weight-bold"
            >
              ${Constants.ROW_HEADERS.BENCHMARK}
            </span>
          </beacon-table-stub>
        </th>
        ${risks.map((risk) => {
          return this.renderTableCell(
            risk.benchmarkValue,
            risk.displayPercentage,
          );
        })}
      </tr>
    `;
  }

  render() {
    if (!this.componentObject) {
      return html``;
    }
    return this.renderComponent(
      html`<section class="risk-table-content">
        <div class="popover-layer">${this.renderRiskGlossaryPopovers()}</div>
        <beacon-table variant="fixed">
          <table>
            <thead>
              ${this.renderTableHead()}
            </thead>
            <tbody>
              ${this.renderTableBody()}
            </tbody>
          </table>
        </beacon-table>
      </section>`,
    );
  }
}
